import { useState } from "react";
import { Navigate } from "react-router-dom";
import { TreePine, Mail, Lock, User, ArrowRight, Search, CreditCard, MessageCircle, AlertCircle, AtSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

type AuthMode = "login" | "signup" | "forgot";

const Auth = () => {
  const { user, loading } = useAuth();
  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const { signIn, signUp, resetPassword } = useAuth();

  if (loading) return null;
  if (user) return <Navigate to="/tableau-de-bord" replace />;

  const passwordMismatch = mode === "signup" && confirmPassword.length > 0 && password !== confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    if (mode === "signup") {
      if (password !== confirmPassword) {
        setFieldErrors({ confirmPassword: "Les mots de passe ne correspondent pas." });
        return;
      }
      if (!displayName.trim() || displayName.trim().length < 3) {
        setFieldErrors({ displayName: "Le pseudonyme doit contenir au moins 3 caractères." });
        return;
      }
    }

    setSubmitting(true);

    if (mode === "login") {
      const { error } = await signIn(email, password);
      if (error) {
        toast({ title: "Erreur de connexion", description: error.message, variant: "destructive" });
      }
    } else if (mode === "signup") {
      const { error, field } = await signUp(email, password, fullName, displayName.trim());
      if (error) {
        if (field) {
          setFieldErrors({ [field]: error.message });
        } else {
          toast({ title: "Erreur d'inscription", description: error.message, variant: "destructive" });
        }
      } else {
        toast({ title: "Vérifiez votre email", description: "Un lien de confirmation vous a été envoyé via ChimayBois." });
      }
    } else if (mode === "forgot") {
      const { error } = await resetPassword(email);
      if (error) {
        toast({ title: "Erreur", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Email envoyé", description: "Si un compte existe avec cet email, un lien de réinitialisation vous a été envoyé." });
      }
    }
    setSubmitting(false);
  };

  const steps = [
    { icon: Search, emoji: "🪵", label: "Trouvez votre bois localement." },
    { icon: CreditCard, emoji: "💳", label: "Réservez pour 10€ (acompte sécurisé)." },
    { icon: MessageCircle, emoji: "💬", label: "Discutez & payez le solde à la livraison." },
  ];

  return (
    <div className="min-h-screen bg-gradient-forest flex items-center justify-center p-4 py-8">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-3">
            <TreePine className="h-8 w-8 text-primary-foreground" />
            <span className="font-display text-2xl font-bold text-primary-foreground">
              Chimay<span className="text-amber">Bois</span>
            </span>
          </div>
          <p className="text-primary-foreground/70 text-sm">
            {mode === "login" && "Connectez-vous à votre compte"}
            {mode === "signup" && "Créez votre compte gratuitement"}
            {mode === "forgot" && "Réinitialiser votre mot de passe"}
          </p>
        </div>

        {/* Comment ça marche - only on signup */}
        {mode === "signup" && (
          <div className="bg-primary-foreground/10 backdrop-blur-sm rounded-xl p-4 mb-5 border border-primary-foreground/20">
            <p className="text-primary-foreground font-display font-semibold text-sm mb-3 text-center">Comment ça marche ?</p>
            <div className="flex justify-between gap-2">
              {steps.map((step, i) => (
                <div key={i} className="flex-1 text-center">
                  <span className="text-2xl block mb-1">{step.emoji}</span>
                  <p className="text-primary-foreground/80 text-xs leading-tight">{step.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-card rounded-xl p-6 shadow-elevated space-y-3.5">
          {mode === "signup" && (
            <>
              {/* Display name */}
              <div>
                <label className="text-sm font-medium mb-1 block">Nom d'utilisateur public</label>
                <div className="relative">
                  <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={displayName}
                    onChange={(e) => { setDisplayName(e.target.value); setFieldErrors((p) => ({ ...p, display_name: "" })); }}
                    placeholder="ex: BoisDuSud"
                    className="pl-10"
                    required
                    minLength={3}
                    maxLength={30}
                  />
                </div>
                {fieldErrors.display_name && (
                  <p className="text-destructive text-xs mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{fieldErrors.display_name}</p>
                )}
                <p className="text-muted-foreground text-xs mt-1">C'est votre pseudonyme visible par tous sur le site.</p>
              </div>

              {/* Full name */}
              <div>
                <label className="text-sm font-medium mb-1 block">Nom et Prénom</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jean Dupont" className="pl-10" required />
                </div>
                <p className="text-muted-foreground text-xs mt-1 italic">Uniquement partagé avec votre interlocuteur une fois la transaction confirmée.</p>
              </div>
            </>
          )}

          {/* Email */}
          <div>
            <label className="text-sm font-medium mb-1 block">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setFieldErrors((p) => ({ ...p, email: "" })); }}
                placeholder="vous@exemple.be"
                className="pl-10"
                required
              />
            </div>
            {fieldErrors.email && (
              <p className="text-destructive text-xs mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{fieldErrors.email}</p>
            )}
          </div>

          {/* Password */}
          {mode !== "forgot" && (
            <div>
              <label className="text-sm font-medium mb-1 block">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-10" required minLength={6} />
              </div>
            </div>
          )}

          {/* Confirm password */}
          {mode === "signup" && (
            <div>
              <label className="text-sm font-medium mb-1 block">Confirmez votre mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setFieldErrors((p) => ({ ...p, confirmPassword: "" })); }}
                  placeholder="••••••••"
                  className={`pl-10 ${passwordMismatch ? "border-destructive ring-destructive" : ""}`}
                  required
                  minLength={6}
                />
              </div>
              {(passwordMismatch || fieldErrors.confirmPassword) && (
                <p className="text-destructive text-xs mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />{fieldErrors.confirmPassword || "Les mots de passe ne correspondent pas."}
                </p>
              )}
            </div>
          )}

          <Button type="submit" className="w-full gap-2" size="lg" disabled={submitting || passwordMismatch}>
            {submitting
              ? "Chargement..."
              : mode === "login"
              ? "Se connecter"
              : mode === "signup"
              ? "Créer mon compte"
              : "Envoyer le lien"}
            <ArrowRight className="h-4 w-4" />
          </Button>

          {mode === "login" && (
            <button
              type="button"
              onClick={() => setMode("forgot")}
              className="block w-full text-center text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              Mot de passe oublié ?
            </button>
          )}

          <p className="text-center text-sm text-muted-foreground">
            {mode === "login" && (
              <>Pas encore de compte ?{" "}
                <button type="button" onClick={() => setMode("signup")} className="text-primary font-medium hover:underline">S'inscrire</button>
              </>
            )}
            {mode === "signup" && (
              <>Déjà inscrit ?{" "}
                <button type="button" onClick={() => setMode("login")} className="text-primary font-medium hover:underline">Se connecter</button>
              </>
            )}
            {mode === "forgot" && (
              <>Retour à la{" "}
                <button type="button" onClick={() => setMode("login")} className="text-primary font-medium hover:underline">connexion</button>
              </>
            )}
          </p>
        </form>
      </div>
    </div>
  );
};

export default Auth;
